import React from 'react';
import { cn } from '../../utils/cn';
import { AlertCircle, X } from 'lucide-react';
import { Button } from './Button';

export interface FormErrorMessageProps {
  error: string | null;
  onDismiss?: () => void;
  className?: string;
  variant?: 'inline' | 'banner';
  showIcon?: boolean;
}

/**
 * Reusable error message component for forms
 * Provides consistent styling and accessibility features
 */
export const FormErrorMessage: React.FC<FormErrorMessageProps> = ({
  error,
  onDismiss,
  className,
  variant = 'inline',
  showIcon = true
}) => {
  if (!error) return null;

  const baseClasses = 'flex items-center gap-2 text-sm text-red-600 dark:text-red-400';
  
  const variantClasses = {
    inline: 'p-2',
    banner: 'p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-md'
  };

  return (
    <div 
      className={cn(baseClasses, variantClasses[variant], className)}
      role="alert"
      aria-live="polite"
    >
      {showIcon && (
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
      )}
      
      <span className="flex-1">
        {error}
      </span>

      {onDismiss && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 h-auto p-1"
          aria-label="Dismiss error"
        >
          <X className="w-3 h-3" />
        </Button>
      )}
    </div>
  );
};